import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Plus, Edit, Trash2, Search, Palette } from 'lucide-react';
import toast from 'react-hot-toast';
import { colorService } from '../../services';

const ColorMaster = () => {
  const [colors, setColors] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingColor, setEditingColor] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors }
  } = useForm();

  const watchedColor = watch('hex');

  // Load colors from API
  useEffect(() => {
    loadColors();
  }, []);

  const loadColors = async () => {
    try {
      setLoading(true);
      const response = await colorService.getAll({ search: searchTerm });
      setColors(response.data || []);
    } catch (error) {
      console.error('Error loading colors:', error);
      toast.error('Failed to load colors');
    } finally {
      setLoading(false);
    }
  };

  // Reload colors when search term changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadColors();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const filteredColors = colors.filter(color =>
    (color.name && color.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (color.hex && color.hex.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const openModal = (color = null) => {
    setEditingColor(color);
    setShowModal(true);
    if (color) {
      reset({
        name: color.name,
        hex: color.hex,
        is_active: color.is_active
      });
    } else {
      reset({ name: '', hex: '#000000', is_active: true });
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingColor(null);
    reset();
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      if (editingColor) {
        // Update color
        await colorService.update(editingColor.id, data);
        toast.success('Color updated successfully!');
      } else {
        // Create new color
        await colorService.create(data);
        toast.success('Color created successfully!');
      }
      closeModal();
      loadColors(); // Reload the list
    } catch (error) {
      console.error('Error saving color:', error);
      toast.error('Failed to save color');
    } finally {
      setLoading(false);
    }
  };

  const deleteColor = async (id) => {
    if (window.confirm('Are you sure you want to delete this color?')) {
      try {
        setLoading(true);
        await colorService.delete(id);
        toast.success('Color deleted successfully!');
        loadColors(); // Reload the list
      } catch (error) {
        console.error('Error deleting color:', error);
        toast.error('Failed to delete color');
      } finally {
        setLoading(false);
      }
    }
  };

  const toggleStatus = async (color) => {
    try {
      setLoading(true);
      await colorService.update(color.id, { 
        name: color.name,
        hex: color.hex,
        is_active: !color.is_active 
      });
      toast.success('Color status updated!');
      loadColors(); // Reload the list
    } catch (error) {
      console.error('Error updating color status:', error);
      toast.error('Failed to update color status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="card">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <h1 style={{ fontSize: '24px', fontWeight: '600', color: '#111827' }}>
            Color Master
          </h1>
          <button
            onClick={() => openModal()}
            className="btn btn-primary"
          >
            <Plus size={18} />
            Add Color
          </button>
        </div>

        {/* Search */}
        <div style={{ marginBottom: '20px', position: 'relative', maxWidth: '300px' }}>
          <Search
            size={18}
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#6b7280'
            }}
          />
          <input
            type="text"
            placeholder="Search colors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-box"
            style={{ paddingLeft: '40px' }}
          />
        </div>

        {/* Colors Table */}
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Color</th>
                <th>Hex Code</th>
                <th>Status</th>
                <th>Created Date</th>
                <th>Updated Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {colors.map((color) => (
                <tr key={color.id}>
                  <td>{color.id}</td>
                  <td style={{ fontWeight: '500' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        backgroundColor: color.hex,
                        border: color.hex === '#FFFFFF' ? '2px solid #e5e7eb' : '1px solid #d1d5db',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {color.hex === '#FFFFFF' && (
                          <Palette size={12} color="#6b7280" />
                        )}
                      </div>
                      {color.name}
                    </div>
                  </td>
                  <td>
                    <code style={{
                      fontFamily: 'monospace',
                      backgroundColor: '#f3f4f6',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}>
                      {color.hex}
                    </code>
                  </td>
                  <td>
                    <button
                      onClick={() => toggleStatus(color)}
                      className={`status-badge ${color.is_active ? 'status-active' : 'status-inactive'}`}
                      style={{ cursor: 'pointer', border: 'none' }}
                      disabled={loading}
                    >
                      {color.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td>{new Date(color.created_at).toLocaleDateString()}</td>
                  <td>{new Date(color.updated_at).toLocaleDateString()}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => openModal(color)}
                        className="btn btn-secondary"
                        style={{ padding: '6px 8px', fontSize: '12px' }}
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => deleteColor(color.id)}
                        className="btn btn-danger"
                        style={{ padding: '6px 8px', fontSize: '12px' }}
                        disabled={loading}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#6b7280'
          }}>
            Loading colors...
          </div>
        )}

        {!loading && colors.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#6b7280'
          }}>
            No colors found.
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingColor ? 'Edit Color' : 'Add New Color'}
              </h2>
              <button onClick={closeModal} className="close-btn">
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="form-group">
                <label className="form-label">Color Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter color name (e.g., Red, Blue)"
                  {...register('name', {
                    required: 'Color name is required',
                    minLength: {
                      value: 2,
                      message: 'Color name must be at least 2 characters'
                    }
                  })}
                />
                {errors.name && (
                  <div className="error-message">{errors.name.message}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Hex Color Code</label>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <input
                    type="color"
                    style={{
                      width: '50px',
                      height: '40px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                    {...register('hex', {
                      required: 'Color code is required'
                    })}
                  />
                  <input
                    type="text"
                    className="form-input"
                    placeholder="#000000"
                    style={{ flex: 1 }}
                    {...register('hex', {
                      required: 'Color code is required',
                      pattern: {
                        value: /^#[0-9A-Fa-f]{6}$/,
                        message: 'Please enter a valid hex color code (e.g., #FF0000)'
                      }
                    })}
                  />
                </div>
                {errors.hex && (
                  <div className="error-message">{errors.hex.message}</div>
                )}
                
                {/* Color Preview */}
                {watchedColor && (
                  <div style={{
                    marginTop: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <span style={{ fontSize: '14px', color: '#6b7280' }}>Preview:</span>
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        backgroundColor: watchedColor,
                        border: '1px solid #d1d5db'
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  {...register('is_active', { required: 'Status is required' })}
                >
                  <option value={true}>Active</option>
                  <option value={false}>Inactive</option>
                </select>
                {errors.is_active && (
                  <div className="error-message">{errors.is_active.message}</div>
                )}
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                marginTop: '24px'
              }}>
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : editingColor ? 'Update Color' : 'Create Color'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ColorMaster;
